import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { DEFAULT_TENANT_ID, type Coupon, type CouponUsage } from '@kawai/shared';
import { db } from '../firebase.js';

const COUPONS_COL = 'coupons';
const COUPON_USAGES_COL = 'coupon_usages';

/**
 * 自テナントのクーポン定義を取得する（管理画面の一覧用 / 新しい順）。
 *
 * Firestore Rules `coupons/{id}` の read は認証済み自テナント。`coupons` には composite index を
 * 設けていないため、`tenantId` のみで引き `createdAt` 降順は JS でソートする（件数が少なく
 * index 追加コストに見合わないため / users.ts と同方針）。
 */
export async function fetchCoupons(
  tenantId: string = DEFAULT_TENANT_ID,
): Promise<Coupon[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(collection(db, COUPONS_COL), where('tenantId', '==', tid));
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<Coupon, 'id'>),
  }));
  return items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/** クーポン 1 件を取得（編集フォームの初期値 / 取得ログ画面の見出し用）。 */
export async function fetchCouponById(id: string): Promise<Coupon | null> {
  const snap = await getDoc(doc(db, COUPONS_COL, id));
  return snap.exists() ? { id: snap.id, ...(snap.data() as Omit<Coupon, 'id'>) } : null;
}

/**
 * あるクーポンの取得ログを新しい順に取得する。
 *
 * Firestore Rules `coupon_usages/{id}` の read は admin + promo 契約自テナント。
 * 既存の composite index `(tenantId ASC, couponId ASC, usedAt DESC)` を使用。
 * 決定論的 doc id（1 ユーザー 1 doc）のため、件数 = ユニーク取得者数。
 */
export async function fetchCouponUsagesByCoupon(
  tenantId: string,
  couponId: string,
): Promise<CouponUsage[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(
    collection(db, COUPON_USAGES_COL),
    where('tenantId', '==', tid),
    where('couponId', '==', couponId),
    orderBy('usedAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<CouponUsage, 'id'>),
  }));
}

/**
 * 自テナントの全クーポン取得ログを取得する（M-68 / 顧客一覧のバッジ日付 + フィルター判定用）。
 *
 * Firestore Rules `coupon_usages/{id}` の read は admin + promo 契約自テナント。
 * `tenantId` 等値のみで引くため composite index 不要（順序は使わず JS 側で集約する）。
 * 件数はクーポン数 × 関与ユーザー数が上限（決定論的 doc id / 配信ログ doc 含む）で、
 * 顧客一覧が全 users を読む既存前提と同オーダー。
 */
export async function fetchCouponUsagesByTenant(
  tenantId: string,
): Promise<CouponUsage[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(collection(db, COUPON_USAGES_COL), where('tenantId', '==', tid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<CouponUsage, 'id'>),
  }));
}

/**
 * ある顧客（lineUserId）のクーポン取得 / 使用状況を新しい順に取得する（M-66 / 顧客詳細用）。
 *
 * firestore.indexes.json に定義済みの composite index `(tenantId ASC, lineUserId ASC,
 * usedAt DESC)` を流用する（index 追加不要 / customer 側の自 usage 読取は doc id 直読みで index 不使用）。
 * 決定論的 doc id（1 ユーザー 1 クーポン 1 doc）のため、件数 = 関与クーポン数。
 */
export async function fetchCouponUsagesByLineUserId(
  tenantId: string,
  lineUserId: string,
): Promise<CouponUsage[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(
    collection(db, COUPON_USAGES_COL),
    where('tenantId', '==', tid),
    where('lineUserId', '==', lineUserId),
    orderBy('usedAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<CouponUsage, 'id'>),
  }));
}

/**
 * M-144: あるクーポンの個人票の件数（= 受け取った人数 / 配信ツリーの右ペイン）。
 * `coupon_usages` は顧客 × クーポンで 1 doc（決定論 doc id）なので件数 = 人数。
 * `tenantId` + `couponId` の等値のみ = composite index 不要。集約で数える（doc を読まない）。
 */
export async function fetchCouponUsageCount(
  tenantId: string,
  couponId: string,
): Promise<number> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const snap = await getCountFromServer(
    query(
      collection(db, COUPON_USAGES_COL),
      where('tenantId', '==', tid),
      where('couponId', '==', couponId),
    ),
  );
  return snap.data().count;
}
